/**
 * CopilotContext — controla la apertura del panel ANC Copilot desde
 * cualquier punto (topbar, command palette, atajos).
 *
 * Importa `../lib/copilot` por efecto de borde para que el planner
 * determinista se **autorregistre** como proveedor DEMO del servicio
 * `copilot` en el runtime selector apenas monta el provider (R17.2), de
 * modo que `runtime.copilot()` resuelva al demo y no al stub. La API
 * pública del contexto no cambia.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

// Efecto de borde: autorregistro del Copilot demo en el runtime selector.
import "../lib/copilot";

interface CopilotContextValue {
  open: boolean;
  openCopilot: (seed?: string) => void;
  closeCopilot: () => void;
  seedQuestion: string | null;
  clearSeed: () => void;
}

const CopilotContext = createContext<CopilotContextValue | null>(null);

export function CopilotProvider({ children }: { children: ReactNode }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [seedQuestion, setSeedQuestion] = useState<string | null>(null);

  const openCopilot = useCallback((seed?: string) => {
    if (seed) setSeedQuestion(seed);
    setOpen(true);
  }, []);
  const closeCopilot = useCallback(() => setOpen(false), []);
  const clearSeed = useCallback(() => setSeedQuestion(null), []);

  const value = useMemo(
    () => ({ open, openCopilot, closeCopilot, seedQuestion, clearSeed }),
    [open, openCopilot, closeCopilot, seedQuestion, clearSeed],
  );

  return <CopilotContext.Provider value={value}>{children}</CopilotContext.Provider>;
}

export function useCopilot(): CopilotContextValue {
  const ctx = useContext(CopilotContext);
  if (ctx === null) {
    throw new Error("useCopilot must be used within <CopilotProvider>");
  }
  return ctx;
}
